import React, { useState, useEffect } from 'react';
import {
  BrowserRouter, Routes, Route,
  Navigate, useLocation
} from 'react-router-dom';
import api from './api';

import Dashboard      from './pages/Dashboard';
import Items          from './pages/Items';
import Billing        from './pages/Billing';
import Reports        from './pages/Reports';
import Customers      from './pages/Customers';
import Suppliers      from './pages/Suppliers';
import Login          from './pages/Login';
import TrialExpired   from './pages/TrialExpired';
import AdminLogin     from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import Navbar         from './components/Navbar';
import Footer         from './components/Footer';
import Settings	      from './pages/Settings';
import StaffManagement  from './pages/StaffManagement';

import Terms   from './pages/Terms';
import Privacy from './pages/Privacy';
import Refund  from './pages/Refund';

// ✅ Reset Password Page
import ResetPassword from './pages/ResetPassword';

// ✅ NEW — Security additions
import AutoLogout from './components/AutoLogout';
import { hasPermission } from './components/RoleGuard';

const YOUR_PHONE = '9159933276';

// ✅ NEW — Secure logout: wipe every trace of the session, not just the token
function secureLogout(handleLogout) {
  localStorage.removeItem('token');
  sessionStorage.clear();
  if (window.history && window.history.replaceState) {
    window.history.replaceState(null, '', window.location.pathname);
  }
  handleLogout();
}

// ✅ NEW — shown when a logged-in staff role hits a route they can't use
function AccessDenied({ role, feature }) {
  return (
    <div style={{ textAlign:'center', padding:'80px 20px', fontFamily:'Arial' }}>
      <div style={{ fontSize:'64px', marginBottom:'16px' }}>🚫</div>
      <h2 style={{ color:'#E74C3C', margin:'0 0 8px' }}>Access Denied!</h2>
      <p style={{ color:'#555', fontSize:'15px', marginBottom:'8px' }}>
        {feature} — உங்களுக்கு access இல்லை!
      </p>
      <p style={{ color:'#888', fontSize:'13px' }}>
        Role: <strong>{role}</strong> | Owner-கிட்ட permission கேளுங்கள்!
      </p>
    </div>
  );
}

function TrialWarningBanner({ daysLeft, isPaid }) {
  const [dismissed, setDismissed] = useState(false);
  if (isPaid || daysLeft > 5 || dismissed) return null;
  const isUrgent = daysLeft <= 2;
  return (
    <div style={{
      background: isUrgent ? '#e74c3c' : '#f39c12',
      color: 'white', padding: '10px 20px',
      display: 'flex', justifyContent: 'space-between',
      alignItems: 'center', flexWrap: 'wrap',
      gap: '10px', fontSize: '14px', fontWeight: '600',
    }}>
      <span>
        {isUrgent ? '🚨' : '⚠️'}{' '}
        Trial {daysLeft <= 0
          ? 'இன்று முடியுது!'
          : `${daysLeft} நாள் மட்டும் மீதம்!`
        } — Rs.299 pay பண்ணுங்கள்!
      </span>
      <div style={{ display: 'flex', gap: '8px' }}>
        <button
          onClick={() => {
            const msg = `JSMSTOCK trial ${daysLeft} நாள் மட்டும் மீதம்!`;
            window.open(
              `https://wa.me/91${9159933276}?text=${encodeURIComponent(msg)}`,
              '_blank'
            );
          }}
          style={{ background:'#25D366', color:'white', border:'none',
            padding:'7px 14px', borderRadius:'6px', cursor:'pointer',
            fontWeight:'700' }}
        >📱 Pay பண்ணுங்கள்</button>
        <button onClick={() => setDismissed(true)}
          style={{ background:'rgba(255,255,255,0.2)', color:'white',
            border:'none', padding:'7px 12px', borderRadius:'6px',
            cursor:'pointer' }}>✕</button>
      </div>
    </div>
  );
}

function ProtectedRoute({ token, children }) {
  if (!token) return <Navigate to="/login" replace />;
  return children;
}

// ✅ NEW — gate a protected route behind a role permission
function PermissionRoute({ token, role, permission, feature, children }) {
  if (!token) return <Navigate to="/login" replace />;
  if (!hasPermission(role, permission)) {
    return <AccessDenied role={role} feature={feature} />;
  }
  return children;
}

function AdminRoute({ adminLoggedIn, onAdminLogin, onAdminLogout }) {
  if (!adminLoggedIn) return <AdminLogin onAdminLogin={onAdminLogin} />;
  return <AdminDashboard onLogout={onAdminLogout} />;
}

function AppInner() {
  const [token, setToken] = useState(
    () => localStorage.getItem('token')
  );
  const [userInfo, setUserInfo]       = useState(null);
  const [adminLoggedIn, setAdminLoggedIn] = useState(
    () => sessionStorage.getItem('admin_logged_in') === 'true'
  );
  const location = useLocation();

  useEffect(() => {
    if (token) {
      api.get('/auth/me')
        .then(res => setUserInfo(res.data))
        .catch(err => {
          if (err.response?.status === 401) {
            localStorage.removeItem('token');
            setToken(null);
            setUserInfo(null);
          }
        });
    } else {
      setUserInfo(null);
    }
  }, [token]);

  useEffect(() => {
    const h = () => setToken(localStorage.getItem('token'));
    window.addEventListener('storage', h);
    return () => window.removeEventListener('storage', h);
  }, []);

  const handleLogin       = (t) => { localStorage.setItem('token', t); setToken(t); };

  // ✅ NEW — best-effort tell the backend to blacklist the token, then wipe locally
  const handleLogout = () => {
    if (token) {
      api.post('/auth/logout').catch(() => {});
    }
    secureLogout(() => { setToken(null); setUserInfo(null); });
  };

  const handleAdminLogin  = ()  => { sessionStorage.setItem('admin_logged_in','true'); setAdminLoggedIn(true); };
  const handleAdminLogout = ()  => { sessionStorage.removeItem('admin_logged_in'); setAdminLoggedIn(false); };

  const isAdminPath = location.pathname.startsWith('/admin');
  const isLegalPath = ['/terms','/privacy','/refund'].includes(location.pathname);
  const isResetPath = location.pathname === '/reset';

  // ✅ NEW — role derived from /auth/me
  const userRole = userInfo
    ? (userInfo.is_staff ? (userInfo.staff_role || 'staff') : 'owner')
    : 'owner';
  const isPremium = userInfo
    ? (userInfo.account_type === 'premium' || userInfo.account_type === 'business')
    : false;

  return (
    <div style={{ display:'flex', flexDirection:'column', minHeight:'100vh' }}>
      {/* ✅ NEW — auto logout after 30 idle minutes, only while a real session is open */}
      {token && !isAdminPath && !isLegalPath && !isResetPath && (
        <AutoLogout onLogout={handleLogout} timeoutMinutes={30} />
      )}

      {/* Navbar — reset page-ல் show வேண்டாம் */}
      {token && !isAdminPath && !isLegalPath && !isResetPath && (
        <>
          <Navbar onLogout={handleLogout} userInfo={userInfo} userRole={userRole} />
          {userInfo && (
            <TrialWarningBanner
              daysLeft={userInfo.days_left}
              isPaid={userInfo.is_paid}
            />
          )}
        </>
      )}

      <div style={{ flex: 1 }}>
        <Routes>
          {/* Legal pages */}
          <Route path="/terms"   element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/refund"  element={<Refund />} />

          {/* Reset Password Route */}
          <Route path="/reset" element={<ResetPassword />} />

          {/* Auth routes */}
          <Route path="/login"
            element={token
              ? <Navigate to="/" replace />
              : <Login onLogin={handleLogin} />}
          />
          <Route path="/trial-expired" element={<TrialExpired />} />
          <Route path="/admin"
            element={<AdminRoute
              adminLoggedIn={adminLoggedIn}
              onAdminLogin={handleAdminLogin}
              onAdminLogout={handleAdminLogout}
            />}
          />

          {/* Protected routes — everyone with a token */}
          <Route path="/" element={
            <ProtectedRoute token={token}><Dashboard /></ProtectedRoute>}
          />
          <Route path="/items" element={
            <ProtectedRoute token={token}><Items userRole={userRole} /></ProtectedRoute>}
          />
          <Route path="/customers" element={
            <ProtectedRoute token={token}><Customers userRole={userRole} /></ProtectedRoute>}
          />
          <Route path="/suppliers" element={
            <ProtectedRoute token={token}><Suppliers userRole={userRole} /></ProtectedRoute>}
          />

          {/* Protected routes — role-gated per ROLE_PERMISSIONS.md */}
          <Route path="/billing" element={
            <PermissionRoute token={token} role={userRole} permission="canBill" feature="பில்லிங்">
              <Billing />
            </PermissionRoute>}
          />
          <Route path="/reports" element={
            <PermissionRoute token={token} role={userRole} permission="canReport" feature="அறிக்கைகள்">
              <Reports />
            </PermissionRoute>}
          />
          <Route path="/settings" element={
            <PermissionRoute token={token} role={userRole} permission="canSettings" feature="Settings">
              <Settings />
            </PermissionRoute>}
          />
          <Route path="/staff" element={
            token
              ? (userRole === 'owner' && isPremium
                  ? <StaffManagement />
                  : <AccessDenied role={userRole} feature="Staff Management" />)
              : <Navigate to="/login" replace />
          } />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>

      <Footer />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppInner />
    </BrowserRouter>
  );
}

export default App;

